/**
 * Stripe Connect Tab Component
 * Manages Stripe Connect integration setup
 */

import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { 
  CreditCard, 
  ExternalLink, 
  CheckCircle, 
  AlertCircle,
  Copy,
  Save
} from 'lucide-react';
import { SuperAdminOnboardingData, getStatusBadgeVariant, getStatusDisplayName } from '@/types/super-admin-onboarding';
import { OnboardingStatus } from '@/types/onboarding';
import { toast } from '@/hooks/use-toast';

interface StripeConnectTabProps {
  data: SuperAdminOnboardingData;
  onFieldUpdate: (field: keyof SuperAdminOnboardingData, value: any) => Promise<void>;
  onBulkFieldUpdate?: (updates: Record<string, any>) => Promise<{ success: boolean; changedCount: number }>;
  onStatusUpdate: (status: OnboardingStatus) => Promise<void>;
}

const StripeConnectTab: React.FC<StripeConnectTabProps> = ({
  data,
  onFieldUpdate,
  onBulkFieldUpdate,
  onStatusUpdate
}) => {
  const [stripeLink, setStripeLink] = useState(data.stripe_connect_link || '');
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Handle stripe link change
  const handleStripeLinkChange = (value: string) => {
    setStripeLink(value);
    setHasChanges(value !== (data.stripe_connect_link || ''));
  };

  // Save stripe link
  const handleSave = async () => {
    try {
      setSaving(true);
      
      if (onBulkFieldUpdate) {
        // Use the new bulk update function
        const result = await onBulkFieldUpdate({ stripe_connect_link: stripeLink });
        
        if (result.changedCount === 0) {
          toast({
            title: "No Changes",
            description: "No fields were modified"
          });
        } else {
          toast({
            title: "Success",
            description: "Stripe Connect link saved successfully"
          });
        }
      } else {
        // Fallback to individual update
        await onFieldUpdate('stripe_connect_link', stripeLink);
        
        toast({
          title: "Success",
          description: "Stripe Connect link saved successfully"
        });
      }
      
      setHasChanges(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save Stripe Connect link",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };


  // Copy link to clipboard
  const handleCopyLink = async () => {
    if (!stripeLink) return;
    
    try {
      await navigator.clipboard.writeText(stripeLink);
      toast({
        title: "Copied",
        description: "Stripe Connect link copied to clipboard"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to copy link to clipboard",
        variant: "destructive"
      });
    }
  };

  // Update status
  const handleStatusUpdate = async (status: OnboardingStatus) => {
    try {
      // Save changes first if any
      if (hasChanges) {
        await handleSave();
      }
      
      await onFieldUpdate('stripe_connection_status', status);
      
      toast({
        title: "Success",
        description: `Stripe Connect status updated to ${getStatusDisplayName(status)}`
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update status",
        variant: "destructive"
      });
    }
  };

  const isComplete = data.stripe_connection_status === OnboardingStatus.COMPLETE;
  const hasLink = Boolean(stripeLink);

  return (
    <div className="space-y-6">
      {/* Header with Status */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <CreditCard className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle>Stripe Connect Setup</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Set up payment processing with Stripe Connect
                </p>
              </div>
            </div>
            <Badge variant={getStatusBadgeVariant(data.stripe_connection_status || OnboardingStatus.NOT_STARTED)}>
              {getStatusDisplayName(data.stripe_connection_status || OnboardingStatus.NOT_STARTED)}
            </Badge>
          </div>
        </CardHeader>
      </Card>


      {/* Stripe Connect Link Management */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Connect Link Management</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="stripe_link">Stripe Connect URL</Label>
            <div className="flex gap-2">
              <Input
                id="stripe_link"
                value={stripeLink}
                onChange={(e) => handleStripeLinkChange(e.target.value)}
                placeholder="https://connect.stripe.com/oauth/authorize?..."
                className="flex-1"
              />
              {hasLink && (
                <Button variant="outline" size="icon" onClick={handleCopyLink}>
                  <Copy className="h-4 w-4" />
                </Button>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              Paste the Stripe Connect authorization URL for the restaurant to complete their setup.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            {hasChanges && (
              <Button onClick={handleSave} disabled={saving}>
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save Link'}
              </Button>
            )}
            
            {hasLink && (
              <Button variant="outline" asChild>
                <a href={stripeLink} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Test Link
                </a>
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Setup Instructions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Setup Instructions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">
                1
              </div>
              <div>
                <h4 className="font-medium">Enter Connect Link</h4>
                <p className="text-sm text-muted-foreground">
                  Paste the Stripe Connect authorization URL for the restaurant.
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">
                2
              </div>
              <div>
                <h4 className="font-medium">Send to Restaurant</h4>
                <p className="text-sm text-muted-foreground">
                  Share the link with the restaurant owner to complete their Stripe account setup.
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">
                3
              </div>
              <div>
                <h4 className="font-medium">Verify Connection</h4>
                <p className="text-sm text-muted-foreground">
                  Once the restaurant completes the setup, verify the connection and mark as complete.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Status Management */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Status Management</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-muted/50 rounded-lg">
            <h4 className="font-medium mb-2">Status Descriptions:</h4>
            <div className="grid grid-cols-1 gap-2 text-sm">
              <div><strong>Not Started:</strong> Stripe Connect setup has not begun</div>
              <div><strong>Awaiting Confirmation:</strong> Onboarding link provided, waiting for completion</div>
              <div><strong>Needs Attention:</strong> Issues or additional information required</div>
              <div><strong>Complete:</strong> Stripe Connect setup is finished and functional</div>
            </div>
          </div>
          
          <div className="space-y-3">
            <h4 className="font-medium">Set Status:</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
              <Button 
                variant={data.stripe_connection_status === OnboardingStatus.NOT_STARTED ? "default" : "outline"}
                onClick={() => handleStatusUpdate(OnboardingStatus.NOT_STARTED)}
                className="justify-start"
              >
                Not Started
              </Button>
              
              <Button 
                variant={data.stripe_connection_status === OnboardingStatus.AWAITING_CONFIRMATION ? "default" : "outline"}
                onClick={() => handleStatusUpdate(OnboardingStatus.AWAITING_CONFIRMATION)}
                className="justify-start"
              >
                Awaiting Confirmation
              </Button>
              
              <Button 
                variant={data.stripe_connection_status === OnboardingStatus.NEEDS_ATTENTION ? "default" : "outline"}
                onClick={() => handleStatusUpdate(OnboardingStatus.NEEDS_ATTENTION)}
                className="justify-start"
              >
                <AlertCircle className="h-4 w-4 mr-2" />
                Needs Attention
              </Button>
              
              <Button 
                variant={data.stripe_connection_status === OnboardingStatus.COMPLETE ? "default" : "outline"}
                onClick={() => handleStatusUpdate(OnboardingStatus.COMPLETE)}
                className="justify-start"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Complete
              </Button>
            </div>
          </div>

          {hasChanges && (
            <div className="pt-2 border-t">
              <Button onClick={handleSave} disabled={saving}>
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default StripeConnectTab;